import { useState, useEffect } from 'react'
import Button from './Button'

const API_URL = import.meta.env.VITE_API_URL || ''

const PRESET_CATEGORIES = [
  {
    name: 'Brand & Theme',
    icon: '🎨',
    suggestions: [
      'My brand theme is zen/minimalist',
      'My brand colors are [your colors]',
      'I prefer calming, nature-inspired designs',
      'My style is modern and clean',
      'I love vintage/retro aesthetics'
    ]
  },
  {
    name: 'Colors',
    icon: '🌈',
    suggestions: [
      'My favorite colors are teal and purple',
      'I prefer earth tones (browns, greens, cream)',
      'I love bright, bold colors',
      'I prefer pastels and soft colors',
      'Black and white designs only'
    ]
  },
  {
    name: 'Machine & Output',
    icon: '⚙️',
    suggestions: [
      'I use a Cricut Maker 3',
      'I use a Brother SE600 embroidery machine',
      'My max embroidery hoop is 4x4 inches',
      'I work with 12x12 vinyl sheets',
      'I do sublimation on tumblers'
    ]
  },
  {
    name: 'Design Preferences',
    icon: '✨',
    suggestions: [
      'Always use transparent backgrounds',
      'Keep designs simple for easy cutting',
      'I prefer bold outlines for weeding',
      'Make text large and readable',
      'Optimize for single-color vinyl'
    ]
  }
]

const UserPreferences = ({ userId, userEmail, onClose }) => {
  const [rules, setRules] = useState([])
  const [newRule, setNewRule] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [activeCategory, setActiveCategory] = useState(null)
  const [galleryOptIn, setGalleryOptIn] = useState(true)

  useEffect(() => {
    fetchRules()
    // Fetch gallery preference
    if (userId) {
      fetch(`${API_URL}/chat/preferences?userId=${userId}`)
        .then(r => r.json())
        .then(data => { if (data.gallery_opt_out !== undefined) setGalleryOptIn(!data.gallery_opt_out) })
        .catch(() => {})
    }
  }, [userId])

  const fetchRules = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${API_URL}/chat/rules/${userId}`)
      if (response.ok) {
        const data = await response.json()
        setRules(data.rules || [])
      }
    } catch (error) {
      console.error('Error fetching rules:', error)
    } finally {
      setLoading(false)
    }
  }

  const addRule = async (ruleText = newRule) => {
    const trimmedRule = ruleText.trim()
    if (!trimmedRule) return
    
    if (rules.some(r => r.toLowerCase() === trimmedRule.toLowerCase())) {
      alert('You already have this preference!')
      return
    }

    try {
      setSaving(true)
      const response = await fetch(`${API_URL}/chat/rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, rule: trimmedRule })
      })
      
      if (response.ok) {
        const data = await response.json()
        setRules(data.rules)
        setNewRule('')
      }
    } catch (error) {
      console.error('Error adding rule:', error)
    } finally {
      setSaving(false)
    }
  }

  const deleteRule = async (index) => {
    try {
      setSaving(true)
      const response = await fetch(`${API_URL}/chat/rules/${userId}/${index}`, {
        method: 'DELETE'
      })
      
      if (response.ok) {
        const data = await response.json()
        setRules(data.rules)
      }
    } catch (error) {
      console.error('Error deleting rule:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      addRule()
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden shadow-xl flex flex-col">
        <div className="p-6 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-zenna flex items-center justify-center">
                <span className="text-white text-lg">✨</span>
              </div>
              <div>
                <h2 className="text-xl font-display font-bold text-slate-900 dark:text-white">
                  My Preferences
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Help Zenna understand your style
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              <svg className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
              Your Preferences ({rules.length})
            </h3>
            
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-zenna-violet border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : rules.length === 0 ? (
              <div className="text-center py-8 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                <p className="text-slate-500 dark:text-slate-400 text-sm mb-2">
                  No preferences yet
                </p>
                <p className="text-xs text-slate-400 dark:text-slate-500">
                  Add some below to help Zenna learn your style!
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {rules.map((rule, index) => (
                  <div
                    key={index}
                    className="group flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-xl"
                  >
                    <span className="text-zenna-violet">✓</span>
                    <span className="flex-1 text-sm text-slate-700 dark:text-slate-300">
                      {rule}
                    </span>
                    <button
                      onClick={() => deleteRule(index)}
                      disabled={saving}
                      className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 transition-all disabled:opacity-50"
                      title="Remove preference"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
              Add a Preference
            </h3>
            <div className="flex gap-2">
              <input
                type="text"
                value={newRule}
                onChange={(e) => setNewRule(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="e.g., My brand colors are teal and purple..."
                className="flex-1 px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-zenna-violet/50 text-sm"
                disabled={saving}
              />
              <Button
                onClick={() => addRule()}
                disabled={!newRule.trim() || saving}
              >
                {saving ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  'Add'
                )}
              </Button>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
              Quick Add Suggestions
            </h3>
            
            <div className="flex flex-wrap gap-2 mb-4">
              {PRESET_CATEGORIES.map((cat) => (
                <button
                  key={cat.name}
                  onClick={() => setActiveCategory(activeCategory === cat.name ? null : cat.name)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    activeCategory === cat.name
                      ? 'bg-zenna-violet text-white'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                >
                  {cat.icon} {cat.name}
                </button>
              ))}
            </div>

            {activeCategory && (
              <div className="space-y-2 animate-in fade-in duration-200">
                {PRESET_CATEGORIES.find(c => c.name === activeCategory)?.suggestions.map((suggestion, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setNewRule(suggestion)
                      if (!suggestion.includes('[')) {
                        addRule(suggestion)
                      }
                    }}
                    disabled={rules.some(r => r.toLowerCase() === suggestion.toLowerCase()) || saving}
                    className={`w-full text-left p-3 rounded-xl border transition-all text-sm ${
                      rules.some(r => r.toLowerCase() === suggestion.toLowerCase())
                        ? 'border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-400 cursor-not-allowed'
                        : 'border-slate-200 dark:border-slate-600 hover:border-zenna-violet/50 hover:bg-zenna-violet/5'
                    }`}
                  >
                    <span className="text-slate-700 dark:text-slate-300">
                      {suggestion}
                    </span>
                    {suggestion.includes('[') && (
                      <span className="block text-xs text-slate-400 mt-1">
                        Click to customize
                      </span>
                    )}
                    {rules.some(r => r.toLowerCase() === suggestion.toLowerCase()) && (
                      <span className="block text-xs text-zenna-emerald mt-1">
                        ✓ Already added
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Gallery Settings */}
        <div className="px-6 py-3 border-t border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Share designs to gallery</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Automatically post your craft patterns to the community gallery</p>
            </div>
            <button
              onClick={async () => {
                try {
                  const newVal = !galleryOptIn
                  setGalleryOptIn(newVal)
                  await fetch(`${API_URL}/chat/preferences`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ userId, gallery_opt_out: !newVal })
                  })
                } catch (e) { console.error('Failed to save gallery preference:', e) }
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${galleryOptIn ? 'bg-zenna-violet' : 'bg-slate-300 dark:bg-slate-600'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${galleryOptIn ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>

        <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
          <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
            Zenna will remember these preferences across all your conversations
          </p>
        </div>
      </div>
    </div>
  )
}

export default UserPreferences